import * as React from "react";
import {
  Button,
  Field,
  Textarea,
  Checkbox,
  Spinner,
  Body1,
  Caption1,
  tokens,
  makeStyles,
} from "@fluentui/react-components";
import { ArrowLeft24Regular, Send24Filled } from "@fluentui/react-icons";

import { TicketSearch } from "./TicketSearch";
import { ErrorBar } from "./ErrorBar";

import { getGlpiClient } from "../services/glpiClient";
import { getMailContext, MailContext } from "../services/outlookContext";
import {
  stripHtml,
  prepareMailHtmlForGlpi,
  buildTicketBodyContent,
} from "../services/htmlStripper";
import { buildTicketWebUrl } from "../types/glpiConstants";

interface Props {
  onBack: () => void;
  onFollowupAdded: (info: { ticketId: number; followupId: number; url: string }) => void;
  onError: (msg: string) => void;
}

const useStyles = makeStyles({
  root: {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
    marginTop: "8px",
  },
  selectedTicket: {
    padding: "6px 8px",
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
  },
  htmlPreview: {
    maxHeight: "160px",
    overflowY: "auto",
    padding: "8px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

export function AddFollowupForm({ onBack, onFollowupAdded, onError }: Props): React.ReactElement {
  const styles = useStyles();

  const [mail, setMail] = React.useState<MailContext | null>(null);
  const [mailError, setMailError] = React.useState<string | null>(null);

  const [selectedTicketId, setSelectedTicketId] = React.useState<number | null>(null);
  const [selectedTicketTitle, setSelectedTicketTitle] = React.useState<string>("");

  const [content, setContent] = React.useState<string>("");
  const [bodyHtml, setBodyHtml] = React.useState<string>("");
  const [useRichBody, setUseRichBody] = React.useState(true);
  const [isPrivate, setIsPrivate] = React.useState(false);

  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    getMailContext()
      .then(async (m) => {
        if (cancelled) return;
        setMail(m);
        const safeHtml = await prepareMailHtmlForGlpi(m.bodyHtml, m.attachments);
        if (cancelled) return;
        const plain = stripHtml(safeHtml || m.bodyText);
        setContent(plain);
        setBodyHtml(safeHtml);
        setUseRichBody(safeHtml.length > 40);
      })
      .catch((e: Error) => {
        if (!cancelled) setMailError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function handleSelectTicket(id: number | null, title: string): void {
    setSelectedTicketId(id);
    setSelectedTicketTitle(title);
  }

  async function handleSubmit(): Promise<void> {
    setFormError(null);
    if (!selectedTicketId) {
      setFormError("Sélectionne d'abord un ticket dans la liste.");
      return;
    }
    const followupContent = buildTicketBodyContent({
      useRichHtml: useRichBody,
      bodyHtml,
      bodyPlain: content,
      senderName: mail?.senderName ?? "",
      senderEmail: mail?.senderEmail ?? "",
    });

    if (!followupContent.trim()) {
      setFormError("Le contenu du suivi est vide.");
      return;
    }

    const client = getGlpiClient();
    if (!client) {
      setFormError("Session GLPI absente.");
      return;
    }

    setSubmitting(true);
    try {
      const created = await client.addTicketFollowup(selectedTicketId, {
        items_id: selectedTicketId,
        itemtype: "Ticket",
        content: followupContent,
        is_private: isPrivate ? 1 : 0,
      });
      const fid = Number(created.id);
      onFollowupAdded({
        ticketId: selectedTicketId,
        followupId: fid,
        url: buildTicketWebUrl(client.apiUrl, selectedTicketId),
      });
    } catch (e) {
      const err = e as Error;
      onError(err.message);
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={onBack} aria-label="Retour">
          Retour
        </Button>
        <Body1><strong>Ajouter un suivi</strong></Body1>
      </div>

      <ErrorBar message={mailError} title="Lecture mail" />
      <ErrorBar message={formError} title="Validation" />

      <TicketSearch selectedId={selectedTicketId} onSelect={handleSelectTicket} />

      {selectedTicketId && (
        <div className={styles.selectedTicket}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Sélectionné :</Caption1>
          <Body1>
            <strong>#{selectedTicketId}</strong> {selectedTicketTitle}
          </Body1>
        </div>
      )}

      <Field label="Contenu du suivi" hint="Issu du corps du mail — modifiable en mode texte.">
        {bodyHtml.length > 0 && (
          <Checkbox
            label="Conserver la mise en page du mail (HTML)"
            checked={useRichBody}
            onChange={(_, d) => setUseRichBody(!!d.checked)}
            style={{ marginBottom: 8 }}
          />
        )}
        {useRichBody && bodyHtml ? (
          <div
            className={styles.htmlPreview}
            dangerouslySetInnerHTML={{
              __html: buildTicketBodyContent({
                useRichHtml: true,
                bodyHtml,
                bodyPlain: content,
                senderName: mail?.senderName ?? "",
                senderEmail: mail?.senderEmail ?? "",
              }),
            }}
          />
        ) : (
          <Textarea
            resize="vertical"
            rows={8}
            value={content}
            onChange={(_, d) => setContent(d.value)}
          />
        )}
      </Field>

      <Checkbox
        label="Marquer comme privé"
        checked={isPrivate}
        onChange={(_, d) => setIsPrivate(!!d.checked)}
      />

      <div className={styles.footer}>
        <Button onClick={onBack} disabled={submitting}>
          Annuler
        </Button>
        <Button
          appearance="primary"
          icon={submitting ? <Spinner size="tiny" /> : <Send24Filled />}
          onClick={handleSubmit}
          disabled={submitting || !selectedTicketId}
        >
          {submitting ? "Envoi…" : "Ajouter le suivi"}
        </Button>
      </div>
    </div>
  );
}
